import React, { useEffect, useState } from "react";
import { SafeAreaView, ScrollView, View, StyleSheet, StatusBar } from "react-native";
import { useDispatch, useSelector } from "react-redux";
import { Grid, Container, Row, Column } from "ui-grid";
import {
  typography,
  Accordion,
  theme,
  Button,
  AccordionGroup,
  OnScreenAlert,
  DoubleStatusBadge2,
} from "ui-components";
import { ReservationCard } from "../../components/new/reservation-card/reservation-card";
import { BannerMessageModal } from "../../components/new/banner-message-modal/banner-message-modal";
import { Footer } from "../../components/new/footer/footer";
import { BottomNav } from "../../components/layout/bottom-nav";
import { domusoResponsiveStyleHelper } from "./utils/domuso-responsive";
import { useNavigation } from "@react-navigation/native";
import { RootState } from "../../redux/reducers";
import { DomusoApiService } from "../../api/DomusoApiService";
import { translate } from "../../i18n";
import { useReservationInformation } from "../../hooks/useReservationInformation";
import { useAppReviewInformation } from "../../hooks/useAppReviewInformation";
import { useBannerMessageInformation } from "../../hooks/useBannerMessageInformation";
import { useHeapAnalytics } from "../../hooks/useHeapAnalytics";
import {
  addReservations,
  setBlacklist,
  setBlacklistRecovery,
  setCurrentReservationId,
  setRecoveryPaymentCleared,
  setUserRoleData,
} from "../../redux/actions/actionCreators";
import automationTestingLocators from "./utils/automation-testing-locators.json";

import { homepage } from "./utils/business-logic-homepage";
import { deriveUnitTitle, formatNumberWithCommas } from "./utils/helper";
import { ReservationOptions } from "../../components/new/reservation-accordion-options/reservation-accordion-options";
import { FullScreenLoader } from "../../components/new/loader/full-screen-loader";
import { RewardsData } from "../../redux/reducers/reward";

export const Homepage = () => {
  const logger = useSelector((state: RootState) => state.logger);
  const dispatch = useDispatch();
  const cognitoUser = useSelector((state: RootState) => state.auth.cognitoUser);
  const domusoApiService = new DomusoApiService(cognitoUser, dispatch);

  const navigation = useNavigation() as any;
  const styles = getStyles();

  const { reservations, isBlacklist, hasError } = useReservationInformation("Home");
  const reservationsFromPaymentSummary = useSelector(
    (state: RootState) => state.reservation.reservations
  );
  const currentUserAppState = useSelector((state: RootState) => state.userAppState.userAppState);
  const rewardsData = useSelector((state: RootState) => state.reward.data) as RewardsData;

  const { bannerMessages, isBannerMessageLoading } = useBannerMessageInformation({ reservations });
  const [isLoading, setLoading] = useState(true);
  const [rewardProgressValue, setRewardProgressValue] = useState(0);
  const [currentPointsBalance, setCurrentPointsBalance] = useState(0);
  const [totalPointsNeededForNextReward, setTotalPointsNeededForNextReward] = useState(0);
  const [remainingPointsNeededForNextRewards, setRemainingPointsNeededForNextRewards] = useState(0);
  const isResident = useSelector((state: RootState) => state.userRole.isResident);
  const redirectUrl = useSelector((state: RootState) => state.userRole.redirectUrl);

  const [selectedReservation, setSelectedReservation] = useState(0);
  const selectedReservationData =
    reservations && reservations?.length > 0 ? reservations[selectedReservation] : {};

  const accordion1Id = "accordion1";
  const createTitleFromReservation = (reservation?: any) => {
    return reservations && reservation
      ? `${reservation?.prop_name}, ${deriveUnitTitle(reservation?.property_unit)}`
      : "Select Unit";
  };
  let [accordion1Title, setAccordion1Title] = useState("Select Unit");
  let [expandedId, setExpandedId] = useState();
  let [selectedOptionAccordionId, setSelectedOptionAccordionId] = useState();
  const onAccordionPress = (newExpandedId: any) => {
    if (homepageAction?.reservationUnitSelectorDropdown.disabled?.length > 0) {
      return;
    }
    setExpandedId((currentExpandedId) =>
      currentExpandedId === newExpandedId ? undefined : newExpandedId
    );
  };
  const onOptionSelected = (
    newExpandedId: any,
    indexOfSelectedReservation: number,
    reservationId: any
  ) => {
    onAccordionPress(newExpandedId);
    setSelectedOptionAccordionId(newExpandedId);
    setSelectedReservation(indexOfSelectedReservation);
    dispatch(setCurrentReservationId(reservationId));
  };

  useEffect(() => {
    const remainingPointsNeededForNextRewards =
      totalPointsNeededForNextReward - currentPointsBalance || 0;
    setRemainingPointsNeededForNextRewards(remainingPointsNeededForNextRewards);
    setRewardProgressValue(currentPointsBalance / totalPointsNeededForNextReward || 0);
  }, [totalPointsNeededForNextReward, currentPointsBalance]);

  useEffect(() => {
    if (!isBannerMessageLoading && (reservations?.length || isCustomer === false || hasError)) {
      const userRoleData = async () => {
        const { redirectUrl, isResident } = await domusoApiService.getUserRoles();
        dispatch(setUserRoleData({ isResident, redirectUrl }));
      };
      userRoleData();
    }
  }, []);

  useEffect(() => {
    if (!isBannerMessageLoading && (reservations?.length || isResident === false || hasError)) {
      setLoading(false);
      if (reservations?.length > 0 && reservations[0].is_blacklisted === 1) {
        if (reservations[0].recovery_payments.length > 0) {
          dispatch(setRecoveryPaymentCleared(false));
        }
        dispatch(setBlacklist(true));
        dispatch(setBlacklistRecovery(true));
      }
      dispatch(setCurrentReservationId(reservations[0]?.id));
      if (reservations?.length > 0) {
        dispatch(addReservations(reservations));
      }
    } else {
      setLoading(true);
    }
  }, [bannerMessages, reservations, isResident, hasError]);

  useEffect(() => {
    setCurrentPointsBalance(rewardsData?.totalPointsCurrent);
    setTotalPointsNeededForNextReward(rewardsData?.pointsToReward);
  }, [rewardsData]);

  //Set AppReview Data.
  useAppReviewInformation();

  //Set Heap Analytics Data
  useHeapAnalytics(reservations);

  let homepageAction = homepage({
    isResident,
    hasError,
    isBlacklist,
    currentReservation: selectedReservationData,
    reservations: reservations,
    bannerMessages,
    currentUserAppState,
    rewardsData,
  });

  const renderReservationCard = () => {
    if (isLoading) {
      if (reservationsFromPaymentSummary[0]?.stopLoading) return;
      return <FullScreenLoader loading={isLoading} />;
    } else {
      return (
        <View style={{ alignItems: "center", marginHorizontal: 0 }}>
          <ReservationCard
            {...selectedReservationData}
            key={selectedReservationData.id}
            service={domusoApiService}
            navigation={navigation}
            homepageAction={homepageAction}
            rewardsData={rewardsData}
          />
        </View>
      );
    }
  };

  const navigateToPaymentFlow = () => {
    navigation.navigate("Actions", {
      screen: "PaymentFlowPaymentDetails",
      params: {
        account_number: selectedReservationData?.account_number,
        accountingDetails: selectedReservationData?.accounting_details,
        allowedPayments: selectedReservationData?.allowed_payments,
        balanceDue: selectedReservationData?.balance_due,
        propertyUnit: selectedReservationData?.property_unit,
        propName: selectedReservationData?.prop_name,
        reservationId: selectedReservationData?.id,
        currentProperty: selectedReservationData?.property,
        currentPropertyProfile: selectedReservationData?.property_profile,
        currentPropertyFeeProfile: selectedReservationData?.property_fee_profile,
        activeAutoPay: selectedReservationData?.active_auto_pay,
        isBlacklisted: selectedReservationData?.is_blacklisted,
        isBlocked: selectedReservationData?.is_blocked,
        status: selectedReservationData?.status,
        experianLinkCohort: selectedReservationData?.cohort?.experianLink,
        isAimPastResident: homepageAction.isAimPastResident,
      },
    });
  };

  const navigateToCreateServiceRequest = () => {
    navigation.navigate("SubmitServiceRequest", {
      ...selectedReservationData,
      account_number: selectedReservationData?.account_number,
      accountingDetails: selectedReservationData?.accounting_details,
      allowedPayments: selectedReservationData?.allowed_payments,
      balanceDue: selectedReservationData?.balance_due,
      propertyUnit: selectedReservationData?.property_unit,
      propName: selectedReservationData?.prop_name,
      reservationId: selectedReservationData?.id,
      currentProperty: selectedReservationData?.property,
      currentPropertyProfile: selectedReservationData?.property_profile,
      currentPropertyFeeProfile: selectedReservationData?.property_fee_profile,
      activeAutoPay: selectedReservationData?.active_auto_pay,
      isBlacklisted: selectedReservationData?.is_blacklisted,
      isBlocked: selectedReservationData?.is_blocked,
    });
  };

  const navigateToRewardsPage = () => {
    navigation.navigate("Actions", {
      screen: "Rewards",
    });
  };
  return (
    <>
      <SafeAreaView style={styles.container}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollableArea}>
          <StatusBar
            hidden={false}
            translucent={false}
            backgroundColor={theme.colors.white}
            barStyle={"dark-content"}
          />
          <View style={{ marginTop: 20 }} />
          <View>
            <Grid>
              <Container>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.header}>
                    <View style={styles.inputRowInner}>
                      {!isLoading && homepageAction?.pointsBalanceDoubleStatusBadge?.visible && (
                        <>
                          <DoubleStatusBadge2
                            leftHeader={translate("homepage.rewardPoints.leftHeader") as string}
                            leftHeaderStyle={{ fontSize: 14 }}
                            leftStatus={`${formatNumberWithCommas(currentPointsBalance)}`}
                            rightHeader={
                              rewardProgressValue >= 1
                                ? (translate("homepage.rewardPoints.rightHeader1") as string)
                                : (`${remainingPointsNeededForNextRewards} ${translate(
                                    "homepage.rewardPoints.rightHeader2"
                                  )}` as string)
                            }
                            rightHeaderStyle={{ fontSize: 14 }}
                            rightStatus={
                              translate("homepage.rewardPoints.rightStatusButton") as string
                            }
                            rightStatusStyle={{ fontFamily: "Lato_700Bold" }}
                            progress={rewardProgressValue >= 1 ? 1 : rewardProgressValue}
                            onPressRightSideButton={navigateToRewardsPage}
                            leftIconName="gift"
                            leftIconSize={20}
                            rightIconName="dollar-circle"
                            {...automationTestingLocators.homePage
                              .currentPointsAndPointsNeededDoubleStatusBadge}
                          />
                          <View style={{ marginTop: 20 }} />
                        </>
                      )}
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={styles.inputRowInner}>
                      {homepageAction?.reservationUnitSelectorDropdown.visible && (
                        <>
                          <AccordionGroup
                            expandedId={expandedId}
                            onAccordionPress={onAccordionPress}>
                            <Accordion
                              title={createTitleFromReservation(selectedReservationData)}
                              id={accordion1Id}
                              expandedId={expandedId}
                              showLeftIcon={false}
                              accordionStyle={{
                                borderRadius: expandedId == accordion1Id ? 20 : 25,
                                borderBottomLeftRadius: expandedId == accordion1Id ? 0 : 25,
                                borderBottomRightRadius: expandedId == accordion1Id ? 0 : 25,
                                paddingTop: 0,
                                paddingBottom: 0,
                                borderColor: theme.colors.stroke,
                              }}
                              childrenContainer={{
                                borderBottomLeftRadius: 20,
                                borderBottomRightRadius: 20,
                              }}
                              titleStyle={{ ...typography.body }}
                              disabled={
                                homepageAction?.reservationUnitSelectorDropdown.disabled?.length > 0
                              }
                              hideInfoIcon={true}
                              {...automationTestingLocators.homePage.selectReservationDropdown}>
                              <>
                                {reservations?.map((reservation: any, index: number) => (
                                  <ReservationOptions
                                    {...reservation}
                                    key={reservation?.id}
                                    accordionId={accordion1Id}
                                    index={index}
                                    reservationsLength={reservations?.length}
                                    selectedReservation={selectedReservation}
                                    onOptionSelected={(item: any) =>
                                      onOptionSelected(item?.accordionID, item?.index, item?.id)
                                    }
                                  />
                                ))}
                              </>
                            </Accordion>
                          </AccordionGroup>
                          <View style={{ marginTop: 20 }} />
                        </>
                      )}
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
                <Row>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                  <Column span={{ xs: 12, sm: 8, md: 8, lg: 8 }} style={styles.inputRow}>
                    <View style={styles.inputRowInner}>
                      {renderReservationCard()}
                      <View style={{ marginBottom: 15 }} />
                    </View>
                  </Column>
                  <Column span={{ xs: false, sm: 2, md: 2, lg: 2 }} />
                </Row>
              </Container>
            </Grid>
          </View>
        </ScrollView>
        <View style={styles.bottomNav} />
        <BottomNav backgroundColor={theme.colors.white}>
          <View style={{ marginTop: 15 }} />
          {homepageAction?.isDoNotAcceptPayment && (
            <Row>
              <Column span={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <View style={styles.inputRowInnerBottomNav}>
                  <ScrollView>
                    <OnScreenAlert
                      alertType="alert"
                      iconName="error"
                      title={translate("homepage.paymentNotAllowedTitle") as string}
                      body={translate("homepage.paymentNotAllowedMessage") as string}
                      {...automationTestingLocators.reservationCard
                        .paymentNotAllowedErrorOnScreenAlert}
                    />
                  </ScrollView>
                  <View style={{ marginBottom: 15 }} />
                </View>
              </Column>
            </Row>
          )}
          {(homepageAction?.isPendingSelfSignupUser || homepageAction?.isPendingApplicantUser) && (
            <Row>
              <Column span={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <View style={styles.inputRowInnerBottomNav}>
                  <ScrollView>
                    <OnScreenAlert
                      alertType="caution"
                      iconName="warning"
                      title={translate("homepage.pendingSelfInviteTitle") as string}
                      body={translate("homepage.pendingSelfInviteMessage") as string}
                      {...automationTestingLocators.reservationCard
                        .reservationPendingSelfSignupErrorOnScreenAlert}
                    />
                  </ScrollView>
                  <View style={{ marginBottom: 15 }} />
                </View>
              </Column>
            </Row>
          )}
          {(homepageAction?.hasError ||
            homepageAction?.hasBalanceError ||
            homepageAction?.missingAllowedPayments) &&
            !homepageAction?.isRetrievingBalance &&
            !homepageAction?.isPendingSelfSignupUser &&
            !homepageAction?.isPendingApplicantUser && (
              <Row>
                <Column span={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                  <View style={styles.inputRowInnerBottomNav}>
                    <ScrollView>
                      <OnScreenAlert
                        alertType="alert"
                        iconName="error"
                        body={translate("homepage.pmsErrorMessage") as string}
                        {...automationTestingLocators.reservationCard
                          .reservationPmsErrorOnScreenAlert}
                      />
                    </ScrollView>
                    <View style={{ marginBottom: 15 }} />
                  </View>
                </Column>
              </Row>
            )}
            {(homepageAction?.hasError ||
            homepageAction?.hasBalanceError ||
            homepageAction?.missingAllowedPayments) &&
            homepageAction?.isRetrievingBalance &&
            !homepageAction?.isPendingSelfSignupUser &&
            !homepageAction?.isPendingApplicantUser && (
              <Row>
                <Column span={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                  <View style={styles.inputRowInnerBottomNav}>
                    <ScrollView>
                      <OnScreenAlert
                        alertType="alert"
                        iconName="error"
                        body={translate("homepage.retrievingBalanceMessage") as string}
                        {...automationTestingLocators.reservationCard
                          .reservationPmsErrorOnScreenAlert}
                      />
                    </ScrollView>
                    <View style={{ marginBottom: 15 }} />
                  </View>
                </Column>
              </Row>
            )}
          <Row>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
            <Column span={{ xs: 12, sm: 12, md: 9, lg: 7 }}>
              {homepageAction.submitMaintenanceRequestButton.visible && (
                <Button
                  text={translate("homepage.submitMaintenanceRequest") as string}
                  style={{ width: "100%", paddingTop: 12, paddingBottom: 12 }}
                  type="secondary"
                  onPress={navigateToCreateServiceRequest}
                  disabled={homepageAction.submitMaintenanceRequestButton.disabled?.length > 0}
                  {...automationTestingLocators.homePage.submitMaintenanceRequestButton}
                />
              )}
              <View style={{ marginTop: 10 }} />
              {homepageAction.makeAPaymentButton.visible && (
                <Button
                  text={translate("homepage.makePayment") as string}
                  style={{ width: "100%", paddingTop: 12, paddingBottom: 12 }}
                  textStyle={{ fontFamily: "Lato_700Bold" }}
                  disabled={homepageAction.makeAPaymentButton.disabled?.length > 0}
                  onPress={navigateToPaymentFlow}
                  {...automationTestingLocators.homePage.makePaymentButton}
                />
              )}
            </Column>
            <Column span={{ xs: false, sm: false, md: 1.5, lg: 2.5 }} />
          </Row>
        </BottomNav>

        <Footer />
        {!isLoading && homepageAction.bannerMessagesModal && (
          <BannerMessageModal bannerMessages={bannerMessages} />
        )}
      </SafeAreaView>
    </>
  );
};

const getStyles = () => {
  return StyleSheet.create({
    container: {
      flex: 1,
      ...domusoResponsiveStyleHelper({
        webPhone: {
          backgroundColor: "#FFFFFF",
        },
        webTablet: {
          backgroundColor: "#FAFAFA",
        },
        webDesktop: {
          backgroundColor: "#FAFAFA",
        },
        iosPhone: {
          backgroundColor: "#FFFFFF",
        },
        iosTablet: {
          backgroundColor: "#FAFAFA",
        },
        androidPhone: {
          backgroundColor: "#FFFFFF",
        },
        androidTablet: {
          backgroundColor: "#FAFAFA",
        },
      }),
      // marginHorizontal: 4,
    },
    scrollView: {
      flexGrow: 0,
    },
    scrollableArea: {
      ...domusoResponsiveStyleHelper({
        webPhone: { backgroundColor: "#FFFFFF", paddingBottom: 10 },
        webTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        webDesktop: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        iosPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        iosTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
        androidPhone: { backgroundColor: "#FFFFFF", paddingBottom: 200 },
        androidTablet: { backgroundColor: "#FAFAFA", paddingBottom: 10 },
      }),
    },
    text: {
      fontSize: 16,
      color: theme.colors.text3,
    },
    noReservationsContainer: {
      display: "flex",
      alignItems: "center",
      marginTop: 10,
    },
    noReservationsImage: {
      width: 94,
      height: 94,
      resizeMode: "contain",
    },
    inputRow: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        webDesktop: {
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",
        },
      }),
    },
    inputRowInner: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        webDesktop: {
          paddingLeft: 30,
          paddingRight: 30,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      }),
    },
    header: {
      ...domusoResponsiveStyleHelper({
        webPhone: { paddingVertical: 3 },
        webTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
        },
        webDesktop: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 40,
          paddingRight: 40,
          backgroundColor: "white",
        },
        iosPhone: {},
        iosTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
        },
        androidPhone: {},
        androidTablet: {
          borderTopRightRadius: 10,
          borderTopLeftRadius: 10,

          borderLeftWidth: 1,
          borderLeftColor: "rgba(102, 102, 102, 0.1)",
          borderTopWidth: 1,
          borderTopColor: "rgba(102, 102, 102, 0.1)",
          borderRightWidth: 1,
          borderRightColor: "rgba(102, 102, 102, 0.1)",

          background: "#FFFFFF",

          paddingTop: 30,
          paddingLeft: 30,
          paddingRight: 30,
          backgroundColor: "white",
        },
      }),
    },
    inputRowInnerBottomNav: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        iosPhone: {},
        iosTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
        androidPhone: {},
        androidTablet: {
          paddingLeft: 10,
          paddingRight: 10,
        },
      }),
    },
    instructionImageContainer: {
      borderWidth: 1,
      padding: 8,
      borderRadius: 10,
      borderColor: theme.colors.stroke,
      height: 64,
      width: 64,
    },
    bottomNav: {
      ...domusoResponsiveStyleHelper({
        webPhone: {},
        webTablet: {},
        webDesktop: {},
        iosPhone: { flex: 1 },
        iosTablet: {},
        androidPhone: { flex: 1 },
        androidTablet: {},
      }),
    },
  });
};
